/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Collapse, Popconfirm, Space, Tag, Tooltip } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import { ColumnProps } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';

import Json from '@totem/components/common/Json';
import RuleGroupDisplay from '@totem/components/rulesEngine/report/ruleGroupDisplay';
import RulesContext from '@totem/components/rulesEngine/report/rulesContext';
import { Rule } from '@totem/components/rulesEngine/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { stringifyArray } from '@totem/utilities/tableFilterUtilities';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const STATUS_COLORS: Record<string, string> = {
  Active: 'success',
  Inactive: 'default',
};

const renderScope = (record: Rule) => {
  if (record.scopeType !== 'User') {
    return <Tag>{record.scopeType || 'System'}</Tag>;
  }

  const scope = record.scope;
  const deepest =
    scope?.controlSystem?.name ||
    scope?.building?.name ||
    scope?.region?.name ||
    scope?.organization?.name ||
    '';

  return (
    <span>
      <Tag color="blue">User</Tag>
      {deepest && <span>{deepest}</span>}
    </span>
  );
};

const RulesTable = () => {
  const {
    rulesData,
    loading,
    input,
    setInput,
    filterOptions,
    onEdit,
    onDelete,
  } = useContext(RulesContext);
  const { userProfile } = useContext(UserProfileContext);
  const navigate = useNavigate();

  const canEdit = permissions.ruleAdministrator.hasPermissions(userProfile);

  const columns: ColumnProps<Rule>[] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      showSorterTooltip: true,
      render: (_, record: Rule) => <span>{record.name}</span>,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) => sortStringAscending(compA.name, compB.name),
    },
    {
      title: 'Domain',
      dataIndex: 'domain',
      key: 'domain',
      showSorterTooltip: true,
      render: (_, record: Rule) => <span>{record.domain}</span>,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) => sortStringAscending(compA.domain, compB.domain),
      filterMultiple: true,
      filteredValue: stringifyArray(input.domain),
      filters:
        isNotNull(filterOptions) && isNotNull(filterOptions.domain)
          ? filterOptions.domain.map((item) => {
              return {
                text: item,
                value: item,
              };
            })
          : [],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      showSorterTooltip: true,
      render: (_, record: Rule) => (
        <Tag color={STATUS_COLORS[record.status] ?? 'default'}>
          {record.status}
        </Tag>
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) => sortStringAscending(compA.status, compB.status),
      filterMultiple: true,
      filteredValue: stringifyArray(input.status),
      filters:
        isNotNull(filterOptions) && isNotNull(filterOptions.status)
          ? filterOptions.status.map((item) => {
              return {
                text: item,
                value: item,
              };
            })
          : [],
    },
    {
      title: 'Sequence',
      dataIndex: 'sequence',
      key: 'sequence',
      showSorterTooltip: true,
      render: (_, record: Rule) => <span>{record.sequence}</span>,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) => (compA.sequence ?? 0) - (compB.sequence ?? 0),
    },
    {
      title: 'Trigger',
      dataIndex: 'trigger',
      key: 'trigger',
      showSorterTooltip: true,
      render: (_, record: Rule) => (
        <span>
          {isNotNull(record.trigger) &&
            record.trigger.map((val, idx) => {
              const triggerKey = `${val}-${idx}`;
              if (idx === 0) {
                return <span key={triggerKey}>{val}</span>;
              }
              return <span key={triggerKey}>, {val}</span>;
            })}
        </span>
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(
          (compA.trigger ?? []).join(','),
          (compB.trigger ?? []).join(','),
        ),
      filterMultiple: true,
      filteredValue: stringifyArray(input.trigger),
      filters:
        isNotNull(filterOptions) && isNotNull(filterOptions.trigger)
          ? filterOptions.trigger.map((item) => {
              return {
                text: item,
                value: item,
              };
            })
          : [],
    },
    {
      title: 'Scope',
      dataIndex: 'scope',
      key: 'scope',
      render: (_, record: Rule) => renderScope(record),
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      render: (_, record: Rule) => (
        <span>
          {isNotNull(record.tags) &&
            record.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)}
        </span>
      ),
      filterMultiple: true,
      filteredValue: stringifyArray(input.tags),
      filters:
        isNotNull(filterOptions) && isNotNull(filterOptions.tags)
          ? filterOptions.tags.map((item) => {
              return {
                text: item,
                value: item,
              };
            })
          : [],
    },
    {
      title: 'Condition',
      dataIndex: 'group',
      key: 'group',
      showSorterTooltip: true,
      render: (_, record: Rule) => <RuleGroupDisplay group={record.group} />,
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'action',
      render: (_, record: Rule) => (
        <Collapse
          items={[
            {
              key: 'Actions',
              label: isNotNull(record.actions)
                ? record.actions.map((val, idx) => {
                    const actionKey = `${val.id ?? 'action'}-${idx}`;
                    if (idx === 0) {
                      return (
                        <span key={actionKey} title={val.data}>
                          {val.type}
                        </span>
                      );
                    }
                    return (
                      <span key={actionKey} title={val.data}>
                        , {val.type}
                      </span>
                    );
                  })
                : '',
              children: <Json value={record} />,
            },
          ]}
        >
          {isNotNull(record.actions) &&
            record.actions.map((val, idx) => {
              const actionKey = `${val.id ?? 'action'}-${idx}`;
              if (idx === 0) {
                return (
                  <span key={actionKey} title={val.data}>
                    {val.type}
                  </span>
                );
              }
              return (
                <span key={actionKey} title={val.data}>
                  , {val.type}
                </span>
              );
            })}
        </Collapse>
      ),
      filterMultiple: true,
      filteredValue: stringifyArray(input.action),
      filters:
        isNotNull(filterOptions) && isNotNull(filterOptions.action)
          ? filterOptions.action.map((item) => {
              return {
                text: item,
                value: item,
              };
            })
          : [],
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (_, record: Rule) => <span>{record.createdBy?.name ?? ''}</span>,
    },
    {
      title: '',
      key: 'rowActions',
      width: canEdit ? 130 : 50,
      render: (_: unknown, record: Rule) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/dashboard/admin/rules/${record.id}`)}
            />
          </Tooltip>
          {canEdit && (
            <>
              <Tooltip title="Edit">
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => onEdit(record.id)}
                />
              </Tooltip>
              <Popconfirm
                title="Delete this rule?"
                onConfirm={() => onDelete(record.id)}
                okText="Delete"
                okButtonProps={{ danger: true }}
              >
                <Tooltip title="Delete">
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  const handleTableChange = (
    updatedPagination: TablePaginationConfig,
    filters: SorterResult<Rule>,
    sorter,
  ) => {
    const { ...params } = filters;

    // @ts-ignore
    setInput({
      ...input,
      ...params,
    });
  };

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={isNotNull(rulesData) ? rulesData : []}
      loading={loading}
      onChange={handleTableChange}
      pagination={{
        total: isNotNull(rulesData) ? rulesData.length : 0,
        showSizeChanger: true,
      }}
      rowKey={(record, index) =>
        `${record.id}-${record.domain}-${record.name}-${index}`
      }
    />
  );
};

export default RulesTable;
